import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { Button, Form, Modal, ProgressBar, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileUpload } from '@fortawesome/free-solid-svg-icons';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const API_BASE = String(
  process.env.REACT_APP_API_BASE || 'https://databank.tcscontrols.com.my'
)
  .trim()
  .replace(/\/+$/, '');
const CHUNK_SIZE_BYTES = 8 * 1024 * 1024;

const normalizePath = (p) =>
  (
    '/' +
    String(p || '')
      .trim()
      .replace(/\\/g, '/')
      .replace(/^\/+/, '')
  ).replace(/\/{2,}/g, '/');

const fmtBytes = (n) => {
  if (!Number.isFinite(n)) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(v < 10 && i ? 2 : v < 100 && i ? 1 : 0)} ${units[i]}`;
};

function ChunkedUploadFile({
  currentPath,
  userId,
  onFileUploaded,
  rootPath,
  sessionPrefix,
}) {
  const [showModal, setShowModal] = useState(false);
  const [file, setFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [progressPct, setProgressPct] = useState(0);
  const [bytesLoaded, setBytesLoaded] = useState(0);
  const [expectedBytes, setExpectedBytes] = useState(0);
  const abortRef = useRef(null);

  const normalizedRoot = normalizePath(rootPath).replace(/\/+$/, '') || '/';
  const clampToRoot = (dest) => {
    const wanted = normalizePath(dest || normalizedRoot).replace(/\/+$/, '');
    if (wanted === normalizedRoot) return normalizedRoot;
    return wanted.startsWith(normalizedRoot + '/')
      ? wanted
      : normalizedRoot;
  };

  const destPath = clampToRoot(currentPath || normalizedRoot);

  const resetState = () => {
    setFile(null);
    setIsUploading(false);
    setProgressPct(0);
    setBytesLoaded(0);
    setExpectedBytes(0);
    abortRef.current = null;
  };

  const makeUploadSessionId = () =>
    `${sessionPrefix}_${Date.now()}_${Math.random().toString(36).slice(2, 12)}`;

  const handleOpen = () => {
    resetState();
    setShowModal(true);
  };

  const handleCancelUpload = () => {
    try {
      abortRef.current?.abort();
      toast.info('Upload canceled.');
    } catch {
      // noop
    }
    setIsUploading(false);
    abortRef.current = null;
  };

  const handleFileSubmit = async (e) => {
    e.preventDefault();

    if (!file) {
      toast.error('Please select a file!');
      return;
    }

    const size = Number(file.size) || 0;
    const totalChunks = Math.max(1, Math.ceil(size / CHUNK_SIZE_BYTES));
    const sessionId = makeUploadSessionId();

    setExpectedBytes(size);
    setBytesLoaded(0);
    setProgressPct(0);
    setIsUploading(true);

    const controller = new AbortController();
    abortRef.current = controller;

    let completedBytes = 0;

    try {
      for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
        const start = chunkIndex * CHUNK_SIZE_BYTES;
        const end = Math.min(size, start + CHUNK_SIZE_BYTES);
        const chunkBlob = file.slice(start, end);

        const fd = new FormData();
        fd.append('chunk', chunkBlob, file.name);
        fd.append('sessionId', sessionId);
        fd.append('path', destPath);
        fd.append('filename', file.name);
        fd.append('chunkIndex', String(chunkIndex));
        fd.append('totalChunks', String(totalChunks));
        if (userId != null) fd.append('userId', String(userId));

        const res = await axios.post(`${API_BASE}/api/upload-chunk`, fd, {
          signal: controller.signal,
          onUploadProgress: (evt) => {
            const loaded = Math.max(0, evt.loaded ?? 0);
            const aggregateLoaded = Math.min(size, completedBytes + loaded);
            setBytesLoaded(aggregateLoaded);
            setProgressPct(
              Math.round((aggregateLoaded / Math.max(size, 1)) * 100)
            );
          },
          maxContentLength: Infinity,
          maxBodyLength: Infinity,
          timeout: 0,
          withCredentials: true,
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });

        if (!res?.data?.ok) {
          throw new Error(res?.data?.error || 'Chunk upload failed');
        }

        completedBytes = Math.min(size, completedBytes + chunkBlob.size);
        setBytesLoaded(completedBytes);
        setProgressPct(
          Math.round((completedBytes / Math.max(size, 1)) * 100)
        );
      }

      const completeRes = await axios.post(
        `${API_BASE}/api/upload-complete`,
        {
          sessionId,
          path: destPath,
          filename: file.name,
          totalChunks,
          userId: userId == null ? undefined : String(userId),
        },
        {
          signal: controller.signal,
          timeout: 0,
          withCredentials: true,
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
        }
      );

      const ok = completeRes?.data?.ok;
      const savedTo =
        completeRes?.data?.uploaded?.to || completeRes?.data?.to || '';
      if (!ok || !savedTo) {
        throw new Error(completeRes?.data?.error || 'Upload failed');
      }

      setBytesLoaded(size);
      setProgressPct(100);

      onFileUploaded?.({
        name: file.name,
        to: savedTo,
        dest: destPath,
        uid: userId,
      });

      toast.success(`"${file.name}" uploaded to ${destPath}`);
      resetState();
      setShowModal(false);
    } catch (err) {
      if (err?.name === 'CanceledError' || axios.isCancel?.(err)) {
        toast.info('Upload canceled.');
      } else {
        const msg =
          err?.response?.data?.error ||
          err?.message ||
          'Upload failed. Please try again.';
        toast.error(msg);
      }
    } finally {
      setIsUploading(false);
      abortRef.current = null;
    }
  };

  const modalTitle = isUploading ? 'Uploading...' : 'Upload File';

  return (
    <>
      <Modal
        show={showModal}
        onHide={() => (!isUploading ? setShowModal(false) : null)}
        centered
        backdrop={isUploading ? 'static' : true}
        keyboard={!isUploading}
      >
        <Modal.Header closeButton={!isUploading}>
          <Modal.Title>{modalTitle}</Modal.Title>
          {isUploading && (
            <div className="ms-auto">
              <Button
                variant="outline-danger"
                size="sm"
                onClick={handleCancelUpload}
              >
                Cancel upload
              </Button>
            </div>
          )}
        </Modal.Header>
        <Modal.Body>
          {isUploading ? (
            <>
              <div className="small text-muted mb-2">
                Destination:&nbsp;<code>{destPath}</code>
              </div>
              {file && (
                <div className="small text-muted mb-2">
                  File:&nbsp;<strong>{file.name}</strong> | Size:&nbsp;
                  <strong>{fmtBytes(expectedBytes)}</strong>
                </div>
              )}
              <div className="d-flex align-items-center gap-2 mb-2">
                <Spinner animation="border" size="sm" />
                <span className="small text-muted">
                  Please keep this window open until upload completes...
                </span>
              </div>
              <ProgressBar now={progressPct} label={`${progressPct}%`} />
              <div className="d-flex justify-content-between small mt-1">
                <span>
                  Sent:&nbsp;<strong>{fmtBytes(bytesLoaded)}</strong>
                </span>
                <span>
                  File size:&nbsp;<strong>{fmtBytes(expectedBytes)}</strong>
                </span>
              </div>
            </>
          ) : (
            <Form onSubmit={handleFileSubmit} encType="multipart/form-data">
              <div className="small text-muted mb-2">
                Destination:&nbsp;<code>{destPath}</code>
              </div>
              <Form.Group controlId="formFile" className="my-2">
                <Form.Control
                  type="file"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  disabled={isUploading}
                />
              </Form.Group>

              {file && (
                <div className="mt-2 small">
                  Selected:&nbsp;<strong>{file.name}</strong>
                  &nbsp;| Size:&nbsp;<strong>{fmtBytes(file.size || 0)}</strong>
                </div>
              )}

              <Button
                type="submit"
                className="w-100 mt-3"
                variant="primary"
                disabled={isUploading || !file}
              >
                Upload File
              </Button>
            </Form>
          )}
        </Modal.Body>
      </Modal>

      <Button
        onClick={handleOpen}
        variant="outline-dark"
        className="d-flex align-items-center rounded-2"
      >
        <FontAwesomeIcon icon={faFileUpload} />
        &nbsp; Upload File
      </Button>

      <ToastContainer position="top-right" autoClose={2000} />
    </>
  );
}

ChunkedUploadFile.propTypes = {
  currentPath: PropTypes.string,
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onFileUploaded: PropTypes.func,
  rootPath: PropTypes.string.isRequired,
  sessionPrefix: PropTypes.string.isRequired,
};

export default ChunkedUploadFile;
